import React from 'react';
import './Pages.css';

const Press: React.FC = () => {
  return (
    <div className="page-container">
      <div className="page-header">
        <h1>Press Kit</h1>
        <p className="page-subtitle">Media Resources for GET AI PORTRAIT</p>
      </div>

      <div className="page-content">
        <section className="content-section">
          <h2>About GET AI PORTRAIT</h2>
          <p>
            <strong>GET AI PORTRAIT</strong> is a premium iOS app that transforms selfies into stunning
            AI-generated portraits across 56+ artistic and professional styles. Using state-of-the-art
            artificial intelligence (Stable Diffusion, FLUX, DALL-E 3), the app delivers Hollywood-quality
            results in under 60 seconds.
          </p>
          <p>
            Designed for social media enthusiasts, content creators, and professionals, GET AI PORTRAIT
            makes premium portrait photography accessible to everyone through an intuitive, privacy-first
            mobile experience.
          </p>
        </section>

        <section className="content-section">
          <h2>Key Facts</h2>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '20px' }}>
            <div>
              <h3>Company</h3>
              <ul>
                <li><strong>Founded:</strong> 2024</li>
                <li><strong>Developer:</strong> Dominik Reif</li>
                <li><strong>Location:</strong> Großenseebach, Germany</li>
                <li><strong>Brand:</strong> DREIF AI</li>
              </ul>
            </div>
            <div>
              <h3>Product</h3>
              <ul>
                <li><strong>Platform:</strong> iOS (iPhone & iPad)</li>
                <li><strong>Launch:</strong> October 2025</li>
                <li><strong>Version:</strong> 1.0 (MVP)</li>
                <li><strong>Languages:</strong> English</li>
              </ul>
            </div>
            <div>
              <h3>Features</h3>
              <ul>
                <li><strong>Styles:</strong> 56+ portrait styles</li>
                <li><strong>Processing:</strong> &lt; 60s per portrait</li>
                <li><strong>Quality:</strong> Full HD output</li>
                <li><strong>Privacy:</strong> GDPR & EU AI Act compliant</li>
              </ul>
            </div>
          </div>
        </section>

        <section className="content-section">
          <h2>Technology</h2>
          <h3>AI Models</h3>
          <ul>
            <li><strong>FLUX 1.1 Pro:</strong> State-of-the-art portrait generation via Replicate API</li>
            <li><strong>GPT-Image-1:</strong> Advanced artistic styles via OpenAI API</li>
            <li><strong>Core ML:</strong> On-device face detection with Apple Vision Framework</li>
            <li><strong>Cloud Processing:</strong> Firebase Functions + CloudKit sync</li>
          </ul>

          <h3>Key Capabilities</h3>
          <ul>
            <li>Face detection accuracy &gt; 95% (Apple Vision Framework)</li>
            <li>Identity preservation threshold: 0.4-0.5 similarity score</li>
            <li>Batch processing: Generate up to 100 portraits from 5-10 selfies</li>
            <li>Background processing with BGTaskScheduler</li>
            <li>Watermarked preview-before-buy conversion funnel</li>
          </ul>
        </section>

        <section className="content-section">
          <h2>Press Assets</h2>
          <p>Download high-resolution media assets for editorial use:</p>

          <h3>Logos & Branding</h3>
          <ul>
            <li><strong>App Icon:</strong> 1024x1024 PNG (Square, Rounded)</li>
            <li><strong>Logo Pack:</strong> SVG, PNG, EPS formats</li>
            <li><strong>Brand Colors:</strong> Primary: #0AD4CF (Turquoise), Secondary: #0A9399</li>
            <li><strong>Typography:</strong> SF Pro (System Font)</li>
          </ul>

          <h3>App Screenshots (iPhone & iPad)</h3>
          <ul>
            <li>Home Screen - Photo Upload Flow</li>
            <li>Style Gallery - 56+ Portrait Styles</li>
            <li>Processing View - Real-time Progress</li>
            <li>Result View - Before/After Comparison</li>
            <li>Gallery View - Portrait Collection</li>
            <li>Premium Paywall - Subscription Options</li>
          </ul>

          <h3>Sample Portraits</h3>
          <ul>
            <li>Cinematic Glow (Hollywood Lighting)</li>
            <li>Neo-Pop Art (Vibrant Comic Style)</li>
            <li>Cyberpunk Samurai (Futuristic Neon)</li>
            <li>Business Professional (Corporate Headshots)</li>
            <li>Fantasy Renaissance (Classical Painting)</li>
          </ul>

          <h3>Video Demos</h3>
          <ul>
            <li>App Walkthrough (60s)</li>
            <li>Portrait Generation Process (30s)</li>
            <li>Before/After Transformations</li>
          </ul>

          <p>
            <strong>Download Press Kit:</strong>{' '}
            <a href="mailto:press@dreif-ai.com?subject=Press Kit Request">
              Request via Email
            </a>
          </p>
        </section>

        <section className="content-section">
          <h2>Boilerplate</h2>
          <div style={{ background: '#f5f5f5', padding: '20px', borderRadius: '8px', fontStyle: 'italic' }}>
            <p>
              <strong>Short Version (50 words):</strong><br />
              GET AI PORTRAIT transforms selfies into stunning AI portraits using advanced machine learning.
              With 56+ styles ranging from Hollywood glamour to cyberpunk aesthetics, the iOS app delivers
              professional-quality results in under 60 seconds. GDPR-compliant and built with privacy-first
              design. Available on the iOS App Store.
            </p>
          </div>

          <div style={{ background: '#f5f5f5', padding: '20px', borderRadius: '8px', fontStyle: 'italic', marginTop: '15px' }}>
            <p>
              <strong>Long Version (150 words):</strong><br />
              GET AI PORTRAIT is a premium iOS application that democratizes professional portrait photography
              through artificial intelligence. Using state-of-the-art AI models (FLUX 1.1 Pro, DALL-E 3, Stable
              Diffusion), the app transforms ordinary selfies into extraordinary portraits across 56+ artistic
              and professional styles.
            </p>
            <p>
              Designed for ages 18-35, the app targets social media enthusiasts, content creators, and
              professionals seeking high-quality visual content. Key features include preview-before-buy
              conversion, CloudKit gallery sync, native iOS sharing, and viral referral mechanics.
            </p>
            <p>
              Built with privacy-first principles, GET AI PORTRAIT is fully GDPR and EU AI Act compliant.
              Biometric face detection is used solely for artistic transformation, not identification.
              Processing happens in &lt; 60 seconds with full HD output quality. Developed by DREIF AI,
              headquartered in Germany.
            </p>
          </div>
        </section>

        <section className="content-section">
          <h2>Target Audience</h2>
          <ul>
            <li><strong>Demographics:</strong> Ages 18-35, 60% female, Instagram/TikTok active users</li>
            <li><strong>Primary Users:</strong> Content creators, social media influencers, dating app users</li>
            <li><strong>Secondary Users:</strong> Professionals needing corporate headshots, creative enthusiasts</li>
            <li><strong>Geographic Focus:</strong> Global (English-speaking markets initially)</li>
          </ul>
        </section>

        <section className="content-section">
          <h2>Monetization</h2>
          <ul>
            <li><strong>Free Tier:</strong> Watermarked preview portraits</li>
            <li><strong>Premium Unlock:</strong> €9.99 per portrait pack (full resolution, no watermark)</li>
            <li><strong>Subscription:</strong> €3.99/month for unlimited portrait generation</li>
            <li><strong>Style Packs:</strong> Additional styles at €2.99 each</li>
            <li><strong>Target ARPU:</strong> €15+ first month</li>
          </ul>
        </section>

        <section className="content-section">
          <h2>Awards & Recognition</h2>
          <p><em>Launching October 2025 - Watch this space!</em></p>
        </section>

        <section className="content-section">
          <h2>Media Contact</h2>
          <div className="contact-box">
            <p>
              <strong>Press & Media Inquiries</strong><br />
              Email: <a href="mailto:press@dreif-ai.com">press@dreif-ai.com</a><br />
              Response time: Within 24 hours
            </p>
            <p>
              <strong>Partnership & Business Development</strong><br />
              Email: <a href="mailto:support@dreif-ai.com">support@dreif-ai.com</a>
            </p>
          </div>
        </section>

        <section className="content-section">
          <h2>Company Information</h2>
          <div className="contact-box">
            <p>
              <strong>GET AI PORTRAIT</strong><br />
              A product by DREIF AI<br />
              <br />
              Dominik Reif<br />
              Waldstraße 14<br />
              91091 Großenseebach<br />
              Germany
            </p>
            <p>
              Website: <a href="https://getaiportrait.com" target="_blank" rel="noopener noreferrer">getaiportrait.com</a><br />
              Company: <a href="https://dreif-ai.com" target="_blank" rel="noopener noreferrer">dreif-ai.com</a><br />
              Support: <a href="mailto:support@dreif-ai.com">support@dreif-ai.com</a>
            </p>
          </div>
        </section>

        <section className="content-section">
          <h2>Social Media</h2>
          <ul>
            <li>Instagram: <a href="https://instagram.com/getaiportrait" target="_blank" rel="noopener noreferrer">@getaiportrait</a></li>
            <li>TikTok: <a href="https://tiktok.com/@getaiportrait" target="_blank" rel="noopener noreferrer">@getaiportrait</a></li>
            <li>Twitter/X: <a href="https://twitter.com/getaiportrait" target="_blank" rel="noopener noreferrer">@getaiportrait</a></li>
            <li>Hashtag: <strong>#GetAIPortrait</strong></li>
          </ul>
        </section>

        <div style={{ marginTop: '40px', padding: '20px', background: '#f0f9ff', borderRadius: '8px' }}>
          <p style={{ margin: 0, fontSize: '14px', color: '#666' }}>
            <strong>Media Usage Guidelines:</strong> Press assets may be used for editorial purposes only.
            Please credit as "GET AI PORTRAIT by DREIF AI" in all publications. High-resolution assets
            available upon request.
          </p>
        </div>
      </div>
    </div>
  );
};

export default Press;